import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Phone, Mail, User, Filter, Users, ShieldCheck, Mic, Square, Loader2, Video, AlertTriangle, FileText, Clock, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminLeads() {
  const { token, user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [salesFilter, setSalesFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const notesTextareaRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterLeads();
  }, [leads, statusFilter, salesFilter, searchQuery]);

  const fetchData = async () => {
    try {
      const [leadsRes, salesRes] = await Promise.all([
        axios.get(`${API_URL}/leads?view_all=true`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/admin/sales-users`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setLeads(leadsRes.data);
      setFilteredLeads(leadsRes.data);
      setSalesUsers(salesRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const filterLeads = () => {
    let filtered = [...leads];

    if (statusFilter !== 'all') {
      filtered = filtered.filter((lead) => lead.status === statusFilter);
    }

    if (salesFilter !== 'all') {
      filtered = filtered.filter((lead) => lead.assigned_to === salesFilter);
    }

    if (searchQuery) {
      filtered = filtered.filter(
        (lead) =>
          lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lead.phone.includes(searchQuery) ||
          (lead.assigned_to_name && lead.assigned_to_name.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    setFilteredLeads(filtered);
  };

  const updateLeadStatus = async (newStatus) => {
    if (!selectedLead) return;

    setUpdating(true);
    try {
      await axios.put(
        `${API_URL}/leads/${selectedLead.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Stato aggiornato con successo');
      setSelectedLead(null);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento dello stato");
    } finally {
      setUpdating(false);
    }
  };

  const updateLeadNotes = async (notes) => {
    if (!selectedLead) return;

    setUpdating(true);
    try {
      await axios.put(
        `${API_URL}/leads/${selectedLead.id}`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Note aggiornate con successo');
      setSelectedLead({ ...selectedLead, notes });
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento delle note");
    } finally {
      setUpdating(false);
    }
  };

  // Funzioni per la registrazione audio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.info('Registrazione in corso... Parla ora!');
    } catch (error) {
      console.error('Errore accesso microfono:', error);
      toast.error('Impossibile accedere al microfono. Verifica i permessi del browser.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await axios.post(`${API_URL}/transcribe`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success && response.data.text) {
        const currentNotes = selectedLead.notes || '';
        const timestamp = new Date().toLocaleString('it-IT');
        const newNotes = currentNotes 
          ? `${currentNotes}\n\n[${timestamp}] ${response.data.text}`
          : `[${timestamp}] ${response.data.text}`;
        
        if (notesTextareaRef.current) {
          notesTextareaRef.current.value = newNotes;
        }
        
        await updateLeadNotes(newNotes);
        toast.success('Audio trascritto e salvato nelle note!');
      } else {
        toast.error('Nessun testo rilevato nell\'audio');
      }
    } catch (error) {
      console.error('Errore trascrizione:', error);
      toast.error('Errore nella trascrizione audio. Riprova.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      nuovo: 'Nuovo',
      messaggio_whatsapp: 'Messaggio WhatsApp',
      telefonata_target: 'Telefonata Target',
      videochiamata: 'Videochiamata',
      convertito: 'Convertito',
      perso: 'Perso'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      nuovo: 'bg-blue-50 text-blue-700 border-blue-200',
      messaggio_whatsapp: 'bg-green-50 text-green-700 border-green-200',
      telefonata_target: 'bg-amber-50 text-amber-700 border-amber-200',
      videochiamata: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      convertito: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      perso: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  const getLostReasonLabel = (reason) => {
    const labels = {
      'non_interessato': 'Non interessato',
      'budget_insufficiente': 'Budget insufficiente',
      'tempistiche_non_adatte': 'Tempistiche non adatte',
      'concorrenza': 'Ha scelto la concorrenza',
      'non_qualificato': 'Non qualificato',
      'non_risponde': 'Non risponde',
      'altro': 'Altro'
    };
    return labels[reason] || reason;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-8 w-8 text-indigo-600" />
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Vista Amministratore</h1>
        </div>
        <p className="text-slate-600">Visualizza e gestisci tutti i lead di tutti i commerciali</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-indigo-600 text-white p-4" data-testid="admin-total-leads">
          <p className="text-indigo-100 text-sm">Lead Totali</p>
          <p className="text-3xl font-bold">{leads.length}</p>
        </Card>
        {salesUsers.map((seller) => (
          <Card key={seller.email} className="bg-white border border-slate-200 p-4" data-testid={`seller-card-${seller.email}`}>
            <div className="flex items-center gap-2 mb-2">
              <User className="h-4 w-4 text-slate-400" />
              <p className="text-sm font-medium text-slate-700 truncate">{seller.name}</p>
            </div>
            <p className="text-2xl font-bold text-slate-900">{seller.total_leads} lead</p>
            <p className="text-xs text-slate-500">{seller.converted_leads} convertiti ({seller.conversion_rate}%)</p>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="bg-white border border-slate-200 shadow-sm mb-6 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Cerca per nome, email, telefono o commerciale..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-11"
              data-testid="admin-search-input"
            />
          </div>
          <div className="w-full md:w-48">
            <Select value={salesFilter} onValueChange={setSalesFilter}>
              <SelectTrigger className="h-11" data-testid="sales-filter">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <SelectValue placeholder="Commerciale" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i commerciali</SelectItem>
                {salesUsers.map((seller) => (
                  <SelectItem key={seller.email} value={seller.email}>
                    {seller.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full md:w-48">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-11" data-testid="admin-status-filter">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  <SelectValue placeholder="Stato" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="nuovo">Nuovo</SelectItem>
                <SelectItem value="messaggio_whatsapp">Messaggio WhatsApp</SelectItem>
                <SelectItem value="telefonata_target">Telefonata Target</SelectItem>
                <SelectItem value="videochiamata">Videochiamata</SelectItem>
                <SelectItem value="convertito">Convertito</SelectItem>
                <SelectItem value="perso">Perso</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Leads List */}
      <div className="grid grid-cols-1 gap-4">
        {filteredLeads.length === 0 ? (
          <Card className="bg-white border border-slate-200 shadow-sm p-12 text-center">
            <p className="text-slate-500">Nessun lead trovato con i filtri selezionati.</p>
          </Card>
        ) : (
          filteredLeads.map((lead) => (
            <Card
              key={lead.id}
              className="bg-white border border-slate-200 shadow-sm hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer p-6"
              onClick={() => setSelectedLead(lead)}
              data-testid="admin-lead-card"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-slate-900">{lead.name}</h3>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                      Assegnato a: {lead.assigned_to_name || 'Non assegnato'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      <span>{lead.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      <span>{lead.phone}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getStatusColor(lead.status)}`}>
                    {getStatusLabel(lead.status)}
                  </span>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Lead Detail Modal */}
      <Dialog open={selectedLead !== null} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-2xl" data-testid="admin-lead-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Dettaglio Lead</DialogTitle>
          </DialogHeader>

          {selectedLead && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Nome</Label>
                  <div className="flex items-center gap-2 text-slate-900">
                    <User className="h-4 w-4 text-slate-400" />
                    <span>{selectedLead.name}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Email</Label>
                  <div className="flex items-center gap-2 text-slate-900">
                    <Mail className="h-4 w-4 text-slate-400" />
                    <span>{selectedLead.email}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Telefono</Label>
                  <div className="flex items-center gap-2 text-slate-900">
                    <Phone className="h-4 w-4 text-slate-400" />
                    <span>{selectedLead.phone}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Assegnato a</Label>
                  <div className="flex items-center gap-2 text-slate-900">
                    <Users className="h-4 w-4 text-slate-400" />
                    <span>{selectedLead.assigned_to_name || 'Non assegnato'}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Stato Attuale</Label>
                  <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getStatusColor(selectedLead.status)}`}>
                    {getStatusLabel(selectedLead.status)}
                  </span>
                </div>
              </div>

              {/* Candidatura Section */}
              {selectedLead.candidatura && Object.keys(selectedLead.candidatura).length > 0 && (
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-slate-600" />
                    Risposte alla Candidatura
                  </h3>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                    {Object.entries(selectedLead.candidatura).map(([question, answer], index) => (
                      <div key={index} className="border-b border-slate-200 last:border-0 pb-3 last:pb-0">
                        <p className="text-sm font-semibold text-slate-700 mb-1">{question}</p>
                        <p className="text-sm text-slate-900">{answer || 'Nessuna risposta'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Note Videochiamata - Se presenti */}
              {selectedLead.video_call_info?.note_videochiamata && (
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Video className="h-5 w-5 text-indigo-600" />
                    Note Videochiamata
                  </h3>
                  <div className="bg-indigo-50 rounded-lg p-4">
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{selectedLead.video_call_info.note_videochiamata}</p>
                  </div>
                </div>
              )}

              {/* Motivo Perdita - Se lead perso */}
              {selectedLead.status === 'perso' && (selectedLead.lost_reason || selectedLead.lost_what_searching) && (
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    Informazioni Perdita Lead
                  </h3>
                  <div className="bg-red-50 rounded-lg p-4 space-y-3">
                    {selectedLead.lost_reason && (
                      <div>
                        <p className="text-sm font-semibold text-red-700">Motivo:</p>
                        <p className="text-sm text-red-900">{getLostReasonLabel(selectedLead.lost_reason)}</p>
                      </div>
                    )}
                    {selectedLead.lost_what_searching && (
                      <div>
                        <p className="text-sm font-semibold text-red-700">Cosa cercava il lead:</p>
                        <p className="text-sm text-red-900">{selectedLead.lost_what_searching}</p>
                      </div>
                    )}
                    {selectedLead.lost_at && (
                      <div>
                        <p className="text-sm font-semibold text-red-700">Data perdita:</p>
                        <p className="text-sm text-red-900">{new Date(selectedLead.lost_at).toLocaleDateString('it-IT')}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Note generali del commerciale */}
              {selectedLead.notes && (
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-slate-600" />
                    Note del Commerciale
                  </h3>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{selectedLead.notes}</p>
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-slate-600" />
                  Timeline
                </h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Creato:</span>
                    <span className="text-slate-900 font-medium">{new Date(selectedLead.created_at).toLocaleString('it-IT')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Ultimo aggiornamento:</span>
                    <span className="text-slate-900 font-medium">{new Date(selectedLead.updated_at).toLocaleString('it-IT')}</span>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Aggiorna Stato</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {['nuovo', 'messaggio_whatsapp', 'telefonata_target', 'videochiamata', 'convertito', 'perso'].map((status) => (
                    <Button
                      key={status}
                      onClick={() => updateLeadStatus(status)}
                      disabled={updating || selectedLead.status === status}
                      variant={selectedLead.status === status ? 'default' : 'outline'}
                      className="text-xs"
                      data-testid={`admin-status-button-${status}`}
                    >
                      {getStatusLabel(status)}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="notes" className="text-sm font-medium text-slate-700">Note</Label>
                  <div className="flex items-center gap-2">
                    {!isRecording && !isTranscribing && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={startRecording}
                        className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50 h-8"
                        data-testid="admin-start-recording-btn"
                      >
                        <Mic className="h-4 w-4" />
                        Nota Audio
                      </Button>
                    )}
                    {isRecording && (
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={stopRecording}
                        className="flex items-center gap-2 animate-pulse h-8"
                        data-testid="admin-stop-recording-btn"
                      >
                        <Square className="h-4 w-4" />
                        Ferma
                      </Button>
                    )}
                    {isTranscribing && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled
                        className="flex items-center gap-2 h-8"
                      >
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Trascrizione...
                      </Button>
                    )}
                  </div>
                </div>
                <Textarea
                  id="notes"
                  ref={notesTextareaRef}
                  defaultValue={selectedLead.notes}
                  onBlur={(e) => {
                    if (e.target.value !== selectedLead.notes) {
                      updateLeadNotes(e.target.value);
                    }
                  }}
                  placeholder="Aggiungi note sul lead... Puoi anche registrare una nota audio!"
                  rows={5}
                  className="resize-none"
                  data-testid="admin-notes-textarea"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Clicca "Nota Audio" per registrare e trascrivere automaticamente le tue note vocali.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
